import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { PushNotifications } from "@capacitor/push-notifications";
import { App } from "@capacitor/app";
import AuthApi from "../api/auth";
import { Capacitor } from "@capacitor/core";

interface PushNotificationContextProps {
  event: PushEvent | null;
  state: string;
  toggleNotifications: (active: boolean) => Promise<void>;
  loading?: boolean;
  userPush: UserPush;
}

interface PushEvent {
  timeStamp: string;
  data: any;
  type: string;
  companyId: string;
}

interface UserPush {
  active: boolean;
  token: string;
}

const PushNotificationContext = createContext<PushNotificationContextProps>({
  event: null,
  state: "not_requested",
  toggleNotifications: async () => {},
  loading: false,
  userPush: { active: false, token: "" },
});

export const usePushNotifications = () => useContext(PushNotificationContext);

export const PushNotificationProvider: React.FC<{
  children: React.ReactNode;
  isAuthenticated?: boolean;
}> = ({ children, isAuthenticated }) => {
  const [userPush, setUserPush] = useState<UserPush>({
    active: false,
    token: "",
  });
  const [event, setEvent] = useState<PushEvent | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [state, setState] = useState("not_granted");
  const appStateListenerRef = useRef<any>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (!Capacitor.isNativePlatform()) {
      setState("not_supported");
      return;
    }

    if (!appStateListenerRef.current && userPush.token) {
      App.addListener("appStateChange", async (props) => {
        await AuthApi.resetPush({
          pushToken: userPush.token,
          active: props.isActive,
        });
      }).then((handle) => {
        appStateListenerRef.current = handle;
      });
    }

    const loadPushEvents = async () => {
      try {
        // set initial state

        const permStatus = await PushNotifications.checkPermissions();
        setState(permStatus.receive);

        // Listen for registration
        await PushNotifications.addListener("registration", async (token) => {
          if (token.value) {
            switch (permStatus.receive) {
              case "denied":
              case "prompt":
                await handlePushToken({ token: token.value, active: false });
                break;
              default:
                await handlePushToken({ token: token.value });
                break;
            }
          }
        });

        // Listen for errors
        await PushNotifications.addListener("registrationError", (err) => {
          console.log("Registration error: ", err.error);
          setState("error");
        });

        // Foreground push
        await PushNotifications.addListener(
          "pushNotificationReceived",
          (notification) => {
            console.log("Push notification received: ", notification);
          }
        );

        // Tap on notification
        await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action) => {
            const { notification } = action;
            const { type, payload } = notification.data;
            setEvent({
              timeStamp: new Date().toString(),
              data: JSON.parse(payload),
              type,
              companyId: `undefined`,
            });
          }
        );

        if (!userPush.token) {
          await PushNotifications.register();
        }
      } catch (e) {
        console.log("Push registration failed:", e);
      }
      return () => {
        PushNotifications.removeAllListeners();
        if (appStateListenerRef.current) {
          appStateListenerRef.current.remove();
          appStateListenerRef.current = null;
        }
      };
    };

    loadPushEvents();
  }, [isAuthenticated, userPush.token]);

  const handlePushToken = async ({
    active,
    token,
  }: {
    active?: boolean;
    token: string;
  }) => {
    setLoading(true);
    const { data, err } = await AuthApi.push({
      active,
      token,
    });
    setLoading(false);
    if (data) {
      setUserPush({
        active: data.push.active,
        token: data.push.token,
      });
    }
    if (err) {
      setState("error");
    }
  };

  async function toggleNotifications(active: boolean) {
    try {
      if (!userPush.token) {
        console.warn("Push token is not available.");
        throw new Error("Push token is not available.");
      }
      if (state === "denied") {
        alert(
          "You need to enable notifications in your device settings for Visito AI."
        );
        return;
      }

      if (state === "granted") {
        await handlePushToken({
          active,
          token: userPush.token,
        });
        return;
      }
      if (state === "prompt") {
        const permStatus = await PushNotifications.requestPermissions();
        setState(permStatus.receive);
        await handlePushToken({
          active,
          token: userPush.token,
        });
        return;
      }
      throw new Error("Invalid state for push notifications");
    } catch (e) {
      alert("Error toggling notifications: " + e);
      console.error("Error disabling notifications:", e);
      setState("error");
    }
  }
  return (
    <PushNotificationContext.Provider
      value={{
        event,
        state,
        toggleNotifications,
        loading,
        userPush,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
};
